import React, { useState } from 'react';
import { withRouter } from 'react-router';
import cloneDeep from 'lodash/cloneDeep';
import TableBody from '@material-ui/core/TableBody';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Drawer from '@material-ui/core/Drawer';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import { withStyles } from '@material-ui/styles';
import { useMutation } from 'react-apollo';
import useSnack from 'hooks/useSnack';
import DatePickerLabel from 'components/common/DatePickerLabel';
import {
  UPDATE_MILESTONE,
  UPDATE_MILESTONE_EXERCISE,
} from 'graphql/student/milestones';
import { ADD_CREDIT } from 'graphql/credits';
import TaskItem from './TaskItem';
import FeedbackModal from './FeedbackModal';
import { STATUS_MILESTONE as STATUS } from 'constant/timeline';
import MeetingFeedbackModal from './MeetingFeedbackModal';
import PRODUCTS from 'constant/product-types';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';

const PRODUCTS_LOOKUP = PRODUCTS.reduce((memo, i) => {
  memo[i.value] = i.label;
  return memo;
}, {});

const styles = (theme) => ({
  paper: {
    maxWidth: 800,
    minWidth: 650,
  },
  flexCenter: {
    display: 'flex',
    alignItems: 'center',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: 8,
    paddingLeft: 16,
  },
  subHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: 8,
    paddingLeft: 16,
  },
  title: {
    flex: 1,
    fontWeight: 600,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  controlRoot: {
    margin: 0,
    flex: 1,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 5,
    marginTop: 5,
  },
  btnSmall: {
    fontSize: 10,
  },
  label: {
    flex: 1,
    fontSize: 14,
  },
  formGroup: {
    marginBottom: 30,
    padding: 16,
    flex: 1,
  },
  checkbox: {
    padding: 0,
    marginRight: 8,
  },
  tableContainer: {
    paddingLeft: 16,
    flex: 1,
  },
  footer: {
    backgroundColor: '#f8f8f8',
    padding: '10px 20px',
    display: 'flex',
    justifyContent: 'space-between',
  },
  appBar: {
    position: 'relative',
  },
  modalBody: {
    padding: 16,
  },
  menuItem: {
    fontSize: 'inherit',
  },
  fieldLabel: {
    marginRight: 8,
  },
  select: {
    fontSize: 14,
  },
});

const getAttendeesOptions = (student) => {
  const attendeesOptions = [];
  attendeesOptions.push({
    label: `${student.fname} ${student.lname} (student)`,
    value: student.user_id,
  });
  student.parents_info.length &&
    student.parents_info[0]?.guardian?.user_first_name &&
    attendeesOptions.push({
      label: `${student.parents_info.length &&
        student.parents_info[0]?.guardian?.user_first_name} ${student
        .parents_info.length &&
        student.parents_info[0]?.guardian?.user_last_name} (parent)`,
      value:
        student.parents_info.length &&
        student.parents_info[0]?.guardian?.user_id,
    });
  student.parents_info.length > 1 &&
    student.parents_info[1]?.guardian?.user_first_name &&
    attendeesOptions.push({
      label: `${student.parents_info.length > 1 &&
        student.parents_info[1]?.guardian?.user_first_name} ${student
        .parents_info.length > 1 &&
        student.parents_info[1]?.guardian?.user_last_name} (parent)`,
      value:
        student.parents_info.length > 1 &&
        student.parents_info[1]?.guardian?.user_id,
    });
  attendeesOptions.push({
    label: `${student.hc_user.user_first_name} ${student.hc_user.user_last_name} (HC)`,
    value: student.hc_id,
  });
  attendeesOptions.push({
    label: `${student.lc_user.user_first_name} ${student.lc_user.user_last_name} (CC)`,
    value: student.lc_id,
  });
  attendeesOptions.push({
    label: `${student.admin.user_first_name} ${student.admin.user_last_name} (Program Supervisor)`,
    value: student.admin.user_id,
  });
  return attendeesOptions;
};

const MilestoneDetails = ({
  onClose,
  milestone,
  classes,
  studentId,
  student,
}) => {
  const [showFeedback, setShowFeedback] = useState(false);
  const [meetingFeedback, showMeetingFeedback] = useState(false);
  const [data, setData] = useState(cloneDeep(milestone));
  const [options, setOptions] = useState(null);
  const [taskState, setTaskState] = useState(null);
  const [updateMilestoneMutation] = useMutation(UPDATE_MILESTONE);
  const [updateTask] = useMutation(UPDATE_MILESTONE_EXERCISE);
  const [useCredit] = useMutation(ADD_CREDIT);
  const snackbar = useSnack();

  React.useEffect(() => {
    setOptions(getAttendeesOptions(student));
  }, [milestone]);

  const handleMilestoneDueChange = (date) => {
    data.due_date = date.format('YYYY-MM-DD');
    setData({ ...data });
    updateMilestone({
      due_date: data.due_date,
    }).then(
      () => {
        window.analytics.track('Due date assigned for a milestone', {
          ...data,
          due_date: data.due_date,
          product: PRODUCTS_LOOKUP[student.product_type]
        });
        snackbar.success('Milestone updated');
      },
      (e) => snackbar.error(e.message),
    );
  };

  const resetDateHandler = () => {
    data.due_date = null;
    setData({ ...data });
    updateMilestone({
      due_date: data.due_date,
    }).then(
      () => {
        window.analytics.track('Due date assigned for a milestone', {
          ...data,
          due_date: data.due_date,
          product: PRODUCTS_LOOKUP[student.product_type]
        });
        snackbar.success('Milestone updated');
      },
      (e) => snackbar.error(e.message),
    );
  }

  const onStatusChange = (e) => {
    const value = e.target.value;
    if (value === 'done') {
      window.analytics.track('Milestone marked complete', {
        ...data,
        status: value,
        product: PRODUCTS_LOOKUP[student.product_type]
      });
      data.status = value;
      setData({ ...data });
      updateMilestone({
        status: data.status,
      });
    } else {
      data.status = value;
      setData({ ...data });
      updateMilestone({
        status: data.status,
      });
    }
  };

  const onSubmitFeedback = (value) => {
    data.status = 'done';
    return updateMilestone({
      milestone_feedback: value,
      status: 'done',
    }).then(() => setShowFeedback(false));
  };

  const updateMilestone = (data) => {
    return updateMilestoneMutation({
      variables: {
        id: milestone.id,
        input: data,
      },
    }).then(
      () => snackbar.success('Milestone updated'),
      (e) => snackbar.error(e.message),
    );
  };

  const onSubmitMeetingSummary = async (values) => {
    try {
      await updateTask({
        variables: {
          id: taskState.id,
          input: values,
        },
      });
      showMeetingFeedback(false);
    } catch (e) {
      snackbar.error(e.message);
    }
  };

  const onChangeTaskStatus = async (task, status) => {
    const shouldUseCredit =
      task.status !== 'done' &&
      (status === 'done' || status === 'noshow') &&
      task.exercise.meeting_type;

    task.status = status;
    setData({ ...data });

    setTaskState(task);

    try {
      await updateTask({
        variables: {
          id: task.id,
          input: {
            status,
            completed_at: status === 'done' ? 'now()' : undefined,
          },
        },
      });
      if (shouldUseCredit) {
        await showMeetingFeedback(true);
        // eslint-disable-next-line react-hooks/rules-of-hooks
        await useCredit({
          variables: {
            input: {
              credit_type: task.exercise.meeting_type,
              user_id: studentId,
              amount: -1,
              description: 'Credit usage',
            },
          },
        });
      }
      snackbar.success('Milestone updated');
    } catch (e) {
      snackbar.error(e.message);
    }
  };

  const handleTaskDueChange = (task, date) => {
    task.due_date = date.toJSON();
    setData({ ...data });
    updateTask({
      variables: {
        id: task.id,
        input: {
          due_date: task.due_date,
        },
      },
    }).then(
      () => {
        window.analytics.track('Due date assigned for exercise', {
          ...task,
          due_date: task.due_date,
          product: PRODUCTS_LOOKUP[student.product_type]
        });
        snackbar.success('Milestone updated');
      },
      (e) => snackbar.error(e.message),
    );
  };

  const tasks = milestone.exercises;

  return (
    <Drawer
      open
      anchor="right"
      onClose={onClose}
      classes={{ paper: classes.paper }}
    >
      <div className={classes.header}>
        <div style={{ flex: 1 }}>
          <Typography gutterBottom variant="h6" className={classes.title}>
            {milestone.name}
          </Typography>
        </div>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </div>
      <div className={classes.subHeader}>
        <Typography
          variant="body2"
          color="textPrimary"
          style={{ marginRight: 8 }}
        >
          Milestone due date:
        </Typography>
        <DatePickerLabel
          onChange={handleMilestoneDueChange}
          value={data.due_date || null}
        />
        <div className={classes.flexCenter}>
          <Typography
            variant="body2"
            color="textPrimary"
            className={classes.fieldLabel}
          >
            Milestone status:
          </Typography>
          <Select
            disableUnderline
            displayEmpty
            renderValue={(val) =>
              STATUS.find((s) => s.value === val)?.label || 'Select'
            }
            classes={{
              select: classes.select,
            }}
            value={data.status}
            onChange={onStatusChange}
          >
            {STATUS.map((i) => (
              <MenuItem
                classes={{ root: classes.menuItem }}
                key={i.value}
                value={i.value}
              >
                {i.label}
              </MenuItem>
            ))}
          </Select>
        </div>
      </div>
      <Box ml={2}>
        <Button
          color="primary"
          variant='outlined'
          onClick={resetDateHandler}
          disabled={data.due_date === null || milestone.status === "done"}
        >
          Reset due date
        </Button>
      </Box>
      <div className={classes.container}>
        <div className={classes.tableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Worksheet name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Due Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tasks.map((i) => {
                return (
                  <>
                    <TaskItem
                      key={i.id}
                      task={i}
                      handleDueDateChange={handleTaskDueChange}
                      onStatusChange={onChangeTaskStatus}
                      classes={classes}
                    />
                  </>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
      {showFeedback && (
        <FeedbackModal
          onSubmit={onSubmitFeedback}
          onClose={() => setShowFeedback(false)}
        />
      )}
      {meetingFeedback && (
        <MeetingFeedbackModal
          student={student}
          task={taskState}
          onSubmit={onSubmitMeetingSummary}
          options={options}
          onClose={() => showMeetingFeedback(false)}
        />
      )}
    </Drawer>
  );
};

export default withStyles(styles)(withRouter(MilestoneDetails));
